import React, { useState } from 'react';
import { useT } from '../lib/i18n';
import { ResponsiveContainer, BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import PageShell from '../components/PageShell';
import { SectionCard, KpiTile, ChartTooltip, PageHeader, QueryState, EmptyNote, CALUM, SERIES, ErrorBox } from '../components/charts';
import DateRangeControl from '../components/DateRangeControl';
import { useApi } from '../hooks/useApi';
import { useDataRange } from '../hooks/useDataRange';
import api from '../lib/api';
import { formatCurrency, formatNumber, formatDate, weekdayLabel } from '../lib/format';
import { todayISO } from '../lib/day';

// green→gold heat scale
const heatColor = (v, max) => {
  if (!max || v <= 0) return '#f1f5f9';
  const t = Math.min(1, v / max);
  const lerp = (a, b) => Math.round(a + (b - a) * t);
  // slate-100 -> calum gold
  return `rgb(${lerp(241, 207)},${lerp(245, 154)},${lerp(249, 64)})`;
};

// "1 jan 2026 – 24 aug 2026" from a backend `window`.
const winLabel = (w) => (w?.start && w?.end ? `${formatDate(w.start)} – ${formatDate(w.end)}` : '');
// Opening hours shown in the hour-based grids.
const OPEN_HOUR = (c) => c.hour >= 8 && c.hour <= 17;
// Colours for the most frequent "popular product" names (sand is too pale for a tint).
const POP_COLORS = SERIES.slice(0, 5);

export default function Traffic() {
  const t = useT();
  const [range, setRange] = useState({ preset: 'today', start_date: todayISO(), end_date: todayISO() });   // open on the current day
  const p = { start_date: range.start_date, end_date: range.end_date };
  const dataRange = useDataRange();
  const deps = [range.start_date, range.end_date];
  const qt = useApi(() => api.quietTimes(p), deps);
  const ds = useApi(() => api.dayStats(p), deps);
  const rd = useApi(() => api.receiptDistribution(p), deps);   // record days
  const hol = useApi(() => api.holidays(), []);
  const pt = useApi(() => api.popularityByTime(p), deps);
  const wb = useApi(() => api.weekdayBasket(p), deps);

  const heat = qt.data?.heatmap || [];
  const slots = heat[0]?.cells?.map((c) => c.label) || [];
  const heatMax = Math.max(0, ...heat.flatMap((r) => r.cells.map((c) => c.avg_receipts)));
  const slotSeries = (qt.data?.slots || []).map((s) => ({ ...s }));
  const slotMax = Math.max(0, ...slotSeries.map((s) => s.avg_receipts));
  const partLabel = (part) => (part === 'AM' ? t('voormiddag') : part === 'PM' ? t('namiddag') : (part || ''));

  // Popular-product grid: number every distinct product (most frequent first) and
  // colour the top few — no hardcoded product names.
  const grid = pt.data?.grid || [];
  const popCounts = {};
  grid.forEach((row) => row.cells.filter(OPEN_HOUR).forEach((c) => { if (c.name) popCounts[c.name] = (popCounts[c.name] || 0) + 1; }));
  const popNames = Object.entries(popCounts).sort((a, b) => b[1] - a[1]).map(([n]) => n);
  const popColor = (name) => { const i = popNames.indexOf(name); return i >= 0 && i < POP_COLORS.length ? POP_COLORS[i] : '#94a3b8'; };
  const popIndex = (name) => popNames.indexOf(name) + 1;
  const weekdays = (wb.data?.weekdays || []).map((w) => ({ ...w, label: weekdayLabel(w.weekday_nl) }));

  return (
    <PageShell page="Drukte & Tijden" accent="green">
      <PageHeader title={t("Drukte & Tijden")} subtitle={t("Wanneer is het druk of stil — per half uur, per uur en per weekdag, plus de recorddagen.")}>
        <DateRangeControl value={range} onChange={setRange} dataRange={dataRange} />
      </PageHeader>

      {ds.error && <ErrorBox error={ds.error} onRetry={ds.reload} />}
      {ds.data?.empty && <EmptyNote />}

      {ds.data && !ds.data.empty && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <KpiTile label={t("Drukste dag")} value={weekdayLabel(ds.data.busiest_day?.weekday_nl)} sub={`${formatDate(ds.data.busiest_day?.date)} · ${formatNumber(ds.data.busiest_day?.receipts)} ${t("bonnen")}`} accent="gold" />
          <KpiTile label={t("Stilste dag")} value={weekdayLabel(ds.data.quietest_day?.weekday_nl)} sub={`${formatDate(ds.data.quietest_day?.date)} · ${formatNumber(ds.data.quietest_day?.receipts)} ${t("bonnen")}`} accent="mint" />
          <KpiTile label={t("Drukste halve dag")} value={`${weekdayLabel(ds.data.busiest_half_day?.weekday_nl)} ${partLabel(ds.data.busiest_half_day?.part)}`} sub={`${formatDate(ds.data.busiest_half_day?.date)} · ${formatCurrency(ds.data.busiest_half_day?.revenue)}`} accent="green" />
          <KpiTile label={t("Gem. bonnen/dag")} value={formatNumber(ds.data.avg_per_day_receipts)} sub={winLabel(ds.data.window) || t("gekozen periode")} accent="gold" />
        </div>
      )}

      <SectionCard title={t("Drukte per half uur (gem. bonnen)")} subtitle={t("Per weekdag · Europe/Brussels — lichte cellen = stil/leeg")}>
        <QueryState q={qt} isEmpty={(d) => !d.heatmap?.length}>
          <div className="overflow-x-auto">
            <table className="text-[11px] border-separate" style={{ borderSpacing: 2 }}>
              <thead>
                <tr>
                  <th className="text-left pr-2 text-slate-400 font-medium sticky left-0 bg-white"></th>
                  {slots.map((s) => <th key={s} className="text-slate-400 font-normal w-7 text-center">{s.endsWith('00') ? s.slice(0, 2) : ''}</th>)}
                </tr>
              </thead>
              <tbody>
                {heat.map((row) => (
                  <tr key={row.weekday}>
                    <td className="pr-2 text-slate-600 font-medium whitespace-nowrap sticky left-0 bg-white">{weekdayLabel(row.weekday_nl)}</td>
                    {row.cells.map((c) => (
                      <td key={c.slot} title={`${weekdayLabel(row.weekday_nl)} ${c.label}: ${c.avg_receipts}`}
                        className="w-7 h-6 rounded text-center align-middle"
                        style={{ background: heatColor(c.avg_receipts, heatMax), color: c.avg_receipts > heatMax * 0.55 ? '#fff' : '#94a3b8' }}>
                        {c.avg_receipts >= 1 ? Math.round(c.avg_receipts) : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </QueryState>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <SectionCard title={t("Gemiddelde drukte per half uur")} subtitle={t("Over alle open dagen")}>
          <QueryState q={qt} isEmpty={(d) => !d.slots?.length}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={slotSeries} margin={{ left: 0, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={14} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="avg_receipts" name={t("Gem. bonnen")} radius={[3, 3, 0, 0]}>
                  {slotSeries.map((s, i) => <Cell key={i} fill={heatColor(s.avg_receipts, slotMax)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </QueryState>
        </SectionCard>

        <SectionCard title={t("Recorddagen")} subtitle={t("Meeste bonnen en hoogste café-omzet in de periode")}>
          <QueryState q={rd} isEmpty={(x) => !x.busiest_days?.length && !x.top_revenue_days?.length}>
            {(x) => (
              <div className="grid grid-cols-2 gap-4 pt-1">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">{t("Meeste bonnen")}</div>
                  {(x.busiest_days || []).map((r) => (
                    <div key={r.date} className="flex justify-between gap-2 text-xs py-1 border-t border-slate-100">
                      <span className="text-slate-600">{formatDate(r.date)} · {weekdayLabel(r.weekday_nl)}</span>
                      <span className="font-semibold tabular-nums">{formatNumber(r.receipts)}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">{t("Hoogste omzet")}</div>
                  {(x.top_revenue_days || []).map((r) => (
                    <div key={r.date} className="flex justify-between gap-2 text-xs py-1 border-t border-slate-100">
                      <span className="text-slate-600">{formatDate(r.date)} · {weekdayLabel(r.weekday_nl)}</span>
                      <span className="font-semibold tabular-nums">{formatCurrency(r.revenue)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </QueryState>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <SectionCard title={t("Gemiddelde bon per uur")} subtitle={t("Lunchuren = hoge bon (volledige maaltijden), koffiemomenten = lage bon")}>
          <QueryState q={pt} isEmpty={(d) => !d.aov_by_hour?.length}>
            {(d) => (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={d.aov_by_hour.filter(OPEN_HOUR)} margin={{ left: 0, right: 8, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
                  <Tooltip content={<ChartTooltip money={['aov']} />} />
                  <Bar dataKey="aov" name={t("Gem. bon")} fill={CALUM.gold} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </QueryState>
        </SectionCard>

        <SectionCard title={t("Bon per weekdag")} subtitle={t("Artikelen per bon (staven) en prijs per artikel (lijn) — het weekend koopt méér, niet duurder")}>
          <QueryState q={wb} isEmpty={(d) => !d.weekdays?.length}>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={weekdays} margin={{ left: 0, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
                <Tooltip content={<ChartTooltip money={['eur_item', 'eur_ticket']} />} />
                <Bar yAxisId="l" dataKey="items_per_ticket" name={t("Artikelen/bon")} fill={CALUM.gold} radius={[3, 3, 0, 0]} />
                <Line yAxisId="r" dataKey="eur_item" name={t("€/artikel")} stroke={CALUM.green} strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </QueryState>
        </SectionCard>
      </div>

      <SectionCard className="mt-4" title={t("Populairste product per uur × weekdag")} subtitle={t("Het vaakst verkochte product per uur — het cijfer verwijst naar de legende onderaan")}>
        <QueryState q={pt} isEmpty={(d) => !d.grid?.length}>
          <div className="overflow-x-auto">
            <table className="text-[11px] border-separate" style={{ borderSpacing: 2 }}>
              <thead>
                <tr>
                  <th className="text-left pr-2 text-slate-400 font-medium sticky left-0 bg-white"></th>
                  {(grid[0]?.cells || []).filter(OPEN_HOUR).map((c) => (
                    <th key={c.hour} className="text-slate-400 font-normal text-center px-1">{String(c.hour).padStart(2, '0')}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.map((row) => (
                  <tr key={row.weekday_nl}>
                    <td className="pr-2 text-slate-600 font-medium whitespace-nowrap sticky left-0 bg-white">{weekdayLabel(row.weekday_nl)}</td>
                    {row.cells.filter(OPEN_HOUR).map((c) => (
                      <td key={c.hour} title={c.name ? `${c.name} · ${c.units}` : ''}
                        className="h-6 min-w-[1.75rem] rounded text-center align-middle font-semibold px-1.5 tabular-nums"
                        style={c.name ? { background: `${popColor(c.name)}33`, color: '#334155' } : { background: '#f8fafc' }}>
                        {c.name ? popIndex(c.name) : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-slate-600">
              {popNames.map((n, i) => (
                <span key={n} className="inline-flex items-center gap-1.5">
                  <span className="inline-block h-3 w-3 rounded-sm" style={{ background: popColor(n) }} />
                  <b className="tabular-nums">{i + 1}</b> {n}
                </span>
              ))}
            </div>
          </div>
        </QueryState>
      </SectionCard>

      <SectionCard className="mt-4" title={t("Feestdagen & uitzonderlijke openingen")}
        subtitle={t("Dagen dat het café open was op een zondag of feestdag (de meeste feestdagen is het gesloten)")}>
        <QueryState q={hol} isEmpty={(d) => !d.exceptional_open_days?.length} empty={t("Nog geen uitzonderlijke openingsdagen in de data.")}>
          {(d) => (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[420px]">
                <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
                  <th className="py-1">{t("Datum")}</th><th>{t("Dag")}</th><th className="text-right">{t("Bonnen")}</th><th className="text-right">{t("Omzet")}</th><th className="text-right">{t("vs gem. dag")}</th>
                </tr></thead>
                <tbody>
                  {d.exceptional_open_days.map((h) => (
                    <tr key={h.date} className="border-t border-slate-100">
                      <td className="py-1.5 whitespace-nowrap">{formatDate(h.date)}</td>
                      <td className="text-slate-500">{t(h.name)}</td>
                      <td className="text-right tabular-nums">{formatNumber(h.receipts)}</td>
                      <td className="text-right tabular-nums">{formatCurrency(h.revenue)}</td>
                      <td className={`text-right tabular-nums ${h.vs_avg_day_revenue_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {h.vs_avg_day_revenue_pct != null ? `${h.vs_avg_day_revenue_pct > 0 ? '+' : ''}${h.vs_avg_day_revenue_pct}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </QueryState>
      </SectionCard>
    </PageShell>
  );
}
